import { IWasmCustomData, TWasmCallback, TWasmId } from "~/interfaces";
import Wasm from "../wasm";
import { IWasmStoreGeometry, TGeomVisualType } from "../store/interfaces";
import { getWasmRequestedCallback } from "../utils";
import { DEF_LINE_STYLES } from "../const";

type TVisualMapping = {
	[key in TGeomVisualType]?: TWasmId;
}

export default class WasmMapGeometry {
	_wasmVisuals: TVisualMapping;

	_wasmIds: Array<TWasmId>;

	_wasm: Wasm;

	_data: IWasmStoreGeometry;

	_customData: IWasmCustomData;

	_geomPoints: Array<number|string>;

	constructor(wasm: Wasm, data: IWasmStoreGeometry) {
		this._wasmVisuals = {};
		this._wasmIds = [];
		this._wasm = wasm;
		this._data = structuredClone(data);
		this._customData = {
			...data.customData,
		};
		this._geomPoints = [];

		this._setGeomPointsFromData();
		this._build();
		this._updateVisual();
	}

	destroy() {
		for (const wasmId of this._wasmIds) {
			this._wasm.mapContent.remove(wasmId);
		}
	}

	updatePoints(points: IWasmStoreGeometry["points"]) {
		this._data.points = points;

		this._setGeomPointsFromData();
		this._updateGeometry();
	}

	updateStrPoints(strPoints: IWasmStoreGeometry["strPoints"]) {
		this._data.strPoints = strPoints;

		this._setGeomPointsFromData();
		this._updateGeometry();
	}

	setVisual(visual: TGeomVisualType) {
		this._data.visual = visual;

		this._updateVisual();
	}

	get wasmIds() {
		return this._wasmIds;
	}

	setVisibility(visible: boolean) {
		this._data.visible = visible;

		this._updateVisual();
	}

	_build() {
		switch (this._data.type) {
			case "measure-current": {
				const lineStyleActive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_CURRENT_ACTIVE);
				const lineStyleDeactive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_CURRENT_DEACTIVE);

				this._wasmVisuals.standard = this._addGeometry(lineStyleActive, "OnClickAndHover", this._getZindex("standard"));
				this._wasmVisuals.deactive = this._addGeometry(lineStyleDeactive, "OnClickAndHover", this._getZindex("deactive"));
				break;
			}

			case "measure-line": {
				const lineStyleActive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_LINE_ACTIVE);
				const lineStyleDeactive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.MEASURE_LINE_DEACTIVE);

				this._wasmVisuals.standard = this._addGeometry(lineStyleActive, "OnClickAndHover", this._getZindex("standard"));
				this._wasmVisuals.deactive = this._addGeometry(lineStyleDeactive, "OnClickAndHover", this._getZindex("deactive"));
				break;
			}

			case "activity-detail": {
				const lineStyleActive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.ACTIVITY_DETAIL);

				this._wasmVisuals.standard = this._addGeometry(lineStyleActive, "None", this._getZindex("standard"));
				break;
			}

			case "poi-geom": {
				const lineStyleActive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.POI_DETAIL_GEOM);

				this._wasmVisuals.standard = this._addGeometry(lineStyleActive, "None", this._getZindex("standard"));
				break;
			}

			case "backpack-route": {
				const lineStyleActive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.BACKPACK_PATH_ACTIVE);
				const lineStyleDeactive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.BACKPACK_PATH_DEACTIVE);

				this._wasmVisuals.standard = this._addGeometry(lineStyleActive, "None", this._getZindex("standard"));
				this._wasmVisuals.deactive = this._addGeometry(lineStyleDeactive, "None", this._getZindex("deactive"));
				break;
			}

			default: {
				// default - zelena nebo mame custom a vlastni style
				if (this._data.type === "custom" && this._data.customStyleHash) {
					const variants = Object.keys(this._data.customStyleHash) as Array<TGeomVisualType>;

					for (const variant of variants) {
						const variantStyle = this._wasm.createGeometryStyle(this._data.customStyleHash[variant]);

						this._wasmVisuals[variant] = this._addGeometry(variantStyle, "OnClickAndHover", this._getZindex(variant));
					}
				} else {
					const lineStyleActive = this._wasm.createGeometryStyle(DEF_LINE_STYLES.DEFAULT);

					this._wasmVisuals.standard = this._addGeometry(lineStyleActive, "None", this._getZindex("standard"));
				}
				break;
			}
		}

		this._wasmIds = [];

		const allVisuals = Object.keys(this._wasmVisuals);

		for (const visual of allVisuals) {
			this._wasmIds.push(this._wasmVisuals[visual]);
		}
	}

	_addGeometry(lineStyle: Module.LineStyle, callbackType: TWasmCallback, zIndex: number) {
		if (!lineStyle) {
			/* eslint-disable-next-line */
			console.error(`Missing line style for type ${this._data.type}!`);

			return -1;
		}

		const callback = getWasmRequestedCallback(this._wasm.module, callbackType);

		return this._wasm.mapContent.addLine(this._geomPoints, this._wasm.module.ObjectLayer.OverAll, zIndex, lineStyle, callback, this._customData);
	}

	_updateVisual() {
		const allVisuals = Object.keys(this._wasmVisuals);
		const visibleIds: Array<TWasmId> = [];
		const hiddenIds: Array<TWasmId> = [];
		let curVisual = this._data.visual;

		if (!(curVisual in this._wasmVisuals)) {
			curVisual = "standard";
		}

		for (const visual of allVisuals) {
			const wasmId = this._wasmVisuals[visual];

			if (this._data.visible) {
				if (curVisual === visual) {
					visibleIds.push(wasmId);
				} else {
					hiddenIds.push(wasmId);
				}
			} else {
				hiddenIds.push(wasmId);
			}
		}

		visibleIds.length > 0 && this._wasm.mapContent.setIsVisible(visibleIds, true);
		hiddenIds.length > 0 && this._wasm.mapContent.setIsVisible(hiddenIds, false);
	}

	_setGeomPointsFromData() {
		this._geomPoints = this._data.strPoints.length
			? this._data.strPoints
			: this._data.points;
	}

	_updateGeometry() {
		for (const wasmId of this._wasmIds) {
			this._wasm.mapContent.updateLineData(wasmId, {
				coordType: this._wasm.module.CoordSystem.Wgs,
				data: this._geomPoints,
			});
		}
	}

	_getZindex(visual: TGeomVisualType) {
		if (this._data.zIndex) {
			return this._data.zIndex[visual] ?? 0;
		}

		return 0;
	}
}
